import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { collection, getDocs, query, where, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { services } from '../../utils/demoData';

const SlotManagement = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [slots, setSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlotForBooking, setSelectedSlotForBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customerName: '',
    customerEmail: '',
    service: '',
    notes: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentDate]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadAppointments(), loadBlockedSlots()]);
    setLoading(false);
  };

  const loadAppointments = async () => {
    try {
      const appointmentsRef = collection(db, 'appointments');
      const querySnapshot = await getDocs(appointmentsRef);
      
      const appointmentsList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        appointmentsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      setAppointments(appointmentsList);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const loadBlockedSlots = async () => {
    try {
      const blockedRef = collection(db, 'blockedSlots');
      const querySnapshot = await getDocs(blockedRef);
      
      const blockedList = [];
      querySnapshot.forEach((doc) => {
        blockedList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setBlockedSlots(blockedList);
    } catch (error) {
      console.error('Error loading blocked slots:', error);
    }
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    startOfWeek.setDate(diff);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const timeSlots = [
    { id: '09:00', time: '9:00 AM' },
    { id: '10:00', time: '10:00 AM' },
    { id: '11:00', time: '11:00 AM' },
    { id: '12:00', time: '12:00 PM' },
    { id: '13:00', time: '1:00 PM' },
    { id: '14:00', time: '2:00 PM' },
    { id: '15:00', time: '3:00 PM' },
    { id: '16:00', time: '4:00 PM' },
    { id: '17:00', time: '5:00 PM' }
  ];

  const getSlotStatus = (date, timeSlot) => {
    const dateStr = date.toISOString().split('T')[0];
    
    // Check if there's an appointment (including all statuses except cancelled)
    const appointment = appointments.find(apt => 
      apt.date === dateStr && 
      apt.time === timeSlot.time && 
      apt.status !== 'cancelled'
    );
    
    // Check if slot is blocked by admin
    const blocked = blockedSlots.find(slot => 
      slot.date === dateStr && slot.timeSlot === timeSlot.id
    );
    

    
    // If there's an appointment, it's booked
    if (appointment) {
      return { 
        type: 'booked', 
        appointment, 
        reason: null,
        customerName: appointment.userName || appointment.userEmail,
        service: appointment.service,
        status: appointment.status
      };
    }
    
    // If admin blocked the slot
    if (blocked) {
      return { type: 'blocked', reason: blocked.reason || 'Blocked by Admin', appointment: null };
    }
    
    // Available slot
    return { type: 'available', appointment: null, reason: null };
  };

  const toggleSlotBlock = async (date, timeSlot) => {
    const dateStr = date.toISOString().split('T')[0];
    const status = getSlotStatus(date, timeSlot);
    
    if (status.type === 'booked') return; // Can't block booked slots
    
    try {
      if (status.type === 'blocked') {
        // Unblock the slot
        const blocked = blockedSlots.find(slot => 
          slot.date === dateStr && slot.timeSlot === timeSlot.id
        );
        if (blocked) {
          await deleteDoc(doc(db, 'blockedSlots', blocked.id));
          setBlockedSlots(blockedSlots.filter(slot => slot.id !== blocked.id));
        }
      } else {
        // Block the slot
        const blockData = {
          date: dateStr,
          timeSlot: timeSlot.id,
          time: timeSlot.time,
          reason: 'Blocked by Admin',
          createdAt: new Date()
        };
        
        const docRef = await addDoc(collection(db, 'blockedSlots'), blockData);
        setBlockedSlots([...blockedSlots, { id: docRef.id, ...blockData }]);
      }
    } catch (error) {
      console.error('Error toggling slot block:', error);
    }
  };

  const handleBookAppointment = (date, timeSlot) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedSlotForBooking({ date: dateStr, timeSlot });
    setShowBookingModal(true);
    setBookingError('');
    setBookingSuccess(false);
  };

  const submitBooking = async () => {
    // Clear previous errors
    setBookingError('');
    
    // Validation
    if (!selectedSlotForBooking || !bookingForm.customerName || !bookingForm.service) {
      setBookingError('Please fill in all required fields');
      return;
    }

    setBookingLoading(true);

    try {
      const appointmentData = {
        userId: 'admin_booking_' + Date.now(), // Special ID for admin bookings
        userEmail: bookingForm.customerEmail || '',
        userName: bookingForm.customerName,
        service: bookingForm.service,
        date: selectedSlotForBooking.date,
        time: selectedSlotForBooking.timeSlot.time,
        notes: bookingForm.notes || '',
        status: 'confirmed', // Admin bookings are automatically confirmed
        createdAt: new Date(),
        updatedAt: new Date(),
        bookedByAdmin: true
      };

      console.log('Creating appointment with data:', appointmentData);
      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      
      // Add to local state
      setAppointments([...appointments, { id: docRef.id, ...appointmentData }]);
      
      // Show success
      setBookingSuccess(true);
      
      // Reset form after a delay
      setTimeout(() => {
        setBookingForm({ customerName: '', customerEmail: '', service: '', notes: '' });
        setShowBookingModal(false);
        setSelectedSlotForBooking(null);
        setBookingSuccess(false);
      }, 2000);
      
      console.log('Admin booking created successfully');
    } catch (error) {
      console.error('Error creating admin booking:', error);
      setBookingError(`Error creating booking: ${error.message}`);
    } finally {
      setBookingLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getSlotColor = (status) => {
    switch (status.type) {
      case 'booked': return '#2196f3';
      case 'blocked': return '#ff9800';
      default: return '#4caf50';
    }
  };

  const getSlotBackground = (status) => {
    switch (status.type) {
      case 'booked': return '#e3f2fd';
      case 'blocked': return '#fff3e0';
      default: return 'transparent';
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const weekDates = getWeekDates();

  return (
    <Layout>
      <div className="analytics-container" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: windowWidth < 768 ? 'stretch' : 'center', 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          gap: windowWidth < 768 ? '1rem' : '0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: windowWidth < 480 ? '0.5rem' : '1rem',
            justifyContent: windowWidth < 768 ? 'center' : 'flex-start'
          }}>
            <button 
              onClick={() => navigateWeek(-1)}
              style={{ 
                background: 'none', 
                border: '1px solid #ddd', 
                borderRadius: '50%', 
                width: windowWidth < 480 ? '36px' : '40px', 
                height: windowWidth < 480 ? '36px' : '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: windowWidth < 480 ? '0.9rem' : '1rem'
              }}
            >
              ←
            </button>
            <h1 style={{ 
              fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
              fontWeight: 'bold', 
              color: '#2e7d32', 
              margin: 0,
              textAlign: windowWidth < 768 ? 'center' : 'left',
              wordBreak: 'break-word'
            }}>
              {windowWidth < 480 ? 'Calendar' : 'Appointment Calendar'}
            </h1>
            <button 
              onClick={() => navigateWeek(1)}
              style={{ 
                background: 'none', 
                border: '1px solid #ddd', 
                borderRadius: '50%', 
                width: windowWidth < 480 ? '36px' : '40px', 
                height: windowWidth < 480 ? '36px' : '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: windowWidth < 480 ? '0.9rem' : '1rem'
              }}
            >
              →
            </button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: windowWidth < 480 ? '0.5rem' : '1rem', 
            alignItems: 'center',
            justifyContent: windowWidth < 768 ? 'center' : 'flex-end',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setViewMode('week')}
                style={{ 
                  fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem',
                  padding: windowWidth < 480 ? '0.5rem 0.75rem' : '0.75rem 1rem'
                }}
              >
                {windowWidth < 480 ? 'Week' : '⊞ Week'}
              </button>
              <button 
                className={`btn ${viewMode === 'day' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setViewMode('day')}
                style={{ 
                  fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem',
                  padding: windowWidth < 480 ? '0.5rem 0.75rem' : '0.75rem 1rem'
                }}
              >
                {windowWidth < 480 ? 'Day' : '📋 Day'}
              </button>
            </div>
            <button 
              className="btn"
              onClick={goToToday}
              style={{ 
                fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem', 
                background: '#2e7d32', 
                color: 'white',
                padding: windowWidth < 480 ? '0.5rem 0.75rem' : '0.75rem 1rem'
              }}
            >
              {windowWidth < 480 ? 'Today' : '📍 Today'}
            </button>
          </div>
        </div>

        {/* Month/Year Display */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h2 style={{ 
            fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem', 
            color: '#333', 
            margin: 0 
          }}>
            {formatMonthYear(currentDate)}
          </h2>
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          justifyContent: 'center', 
          gap: windowWidth < 768 ? '0.75rem' : '2rem', 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          padding: windowWidth < 480 ? '0.75rem' : '1rem',
          background: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: windowWidth < 768 ? 'center' : 'flex-start' }}>
            <div style={{ 
              width: windowWidth < 480 ? '14px' : '16px', 
              height: windowWidth < 480 ? '14px' : '16px', 
              background: '#2196f3', 
              borderRadius: '4px' 
            }}></div>
            <span style={{ fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem' }}>
              {windowWidth < 480 ? 'Booked' : '📅 Booked Appointment'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: windowWidth < 768 ? 'center' : 'flex-start' }}>
            <div style={{ 
              width: windowWidth < 480 ? '14px' : '16px', 
              height: windowWidth < 480 ? '14px' : '16px', 
              background: '#ff9800', 
              borderRadius: '4px' 
            }}></div>
            <span style={{ fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem' }}>
              {windowWidth < 480 ? 'Blocked' : '🚫 Blocked Slot'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: windowWidth < 768 ? 'center' : 'flex-start' }}>
            <div style={{ 
              width: windowWidth < 480 ? '14px' : '16px', 
              height: windowWidth < 480 ? '14px' : '16px', 
              background: 'transparent', 
              border: '2px dashed #4caf50', 
              borderRadius: '4px' 
            }}></div>
            <span style={{ fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem' }}>
              {windowWidth < 480 ? 'Available' : '✅ Available Slot'}
            </span>
          </div>
        </div>

        {/* Business Hours Info */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          padding: windowWidth < 480 ? '0.5rem' : '0.75rem',
          background: '#e3f2fd',
          borderRadius: '8px',
          fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem',
          color: '#1976d2',
          lineHeight: 1.4
        }}>
          {windowWidth < 480 ? (
            <>
              ℹ️ <strong>Hours:</strong> Mon-Fri, 9AM-5PM<br />
              <strong>Weekends:</strong> Closed
            </>
          ) : (
            <>
              ℹ️ <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM (1-hour slots) • <strong>Weekend Policy:</strong> Closed on Saturdays and Sundays
            </>
          )}
        </div>

        {/* Calendar Grid */}
        <div className="card" style={{ 
          padding: 0, 
          overflow: windowWidth < 768 ? 'visible' : 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
              Loading calendar...
            </div>
          ) : (
            <>
              {windowWidth >= 768 ? (
                // Desktop Calendar Grid
                <div>
                  {/* Calendar Header */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '100px repeat(7, 1fr)',
                    background: '#f5f5f5',
                    borderBottom: '2px solid #ddd'
                  }}>
                    <div style={{ padding: '1rem', fontWeight: 'bold', fontSize: '0.875rem', color: '#666' }}>
                      Time
                    </div>
                    {weekDates.map((date, index) => (
                      <div 
                        key={index}
                        style={{ 
                          padding: '1rem', 
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          color: date.toDateString() === new Date().toDateString() ? '#2e7d32' : '#333',
                          background: date.toDateString() === new Date().toDateString() ? '#e8f5e8' : 'transparent'
                        }}
                      >
                        <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        <div style={{ fontSize: '1.25rem', marginTop: '0.25rem' }}>
                          {date.getDate()}
                        </div>
                      </div>
                    ))}
                  </div>

              {/* Calendar Body */}
              {timeSlots.map((timeSlot) => (
                <div 
                  key={timeSlot.id}
                  style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '100px repeat(7, 1fr)',
                    borderBottom: '1px solid #eee',
                    minHeight: '80px'
                  }}
                >
                  {/* Time Column */}
                  <div style={{ 
                    padding: '1rem', 
                    background: '#fafafa',
                    borderRight: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: '#666'
                  }}>
                    {timeSlot.time}
                  </div>

                  {/* Day Columns */}
                  {weekDates.map((date, dayIndex) => {
                    const status = getSlotStatus(date, timeSlot);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    
                    return (
                      <div 
                        key={dayIndex}
                        style={{ 
                          padding: '0.5rem',
                          borderRight: dayIndex < 6 ? '1px solid #eee' : 'none',
                          background: isWeekend ? '#f9f9f9' : 'white',
                          position: 'relative',
                          minHeight: '80px'
                        }}
                      >
                        {isWeekend ? (
                          <div style={{ 
                            textAlign: 'center', 
                            color: '#999', 
                            fontSize: '0.75rem',
                            padding: '1rem 0.5rem'
                          }}>
                            Closed
                          </div>
                        ) : (
                          <div>
                            {status.type === 'booked' ? (
                              <div style={{
                                background: getSlotBackground(status),
                                border: `2px solid ${getSlotColor(status)}`,
                                borderRadius: '6px',
                                padding: '0.5rem',
                                fontSize: '0.75rem',
                                height: '100%',
                                minHeight: '60px'
                              }}>
                                <div style={{ fontWeight: 'bold', color: getSlotColor(status), marginBottom: '0.25rem' }}>
                                  {status.customerName}
                                </div>
                                <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '0.25rem' }}>
                                  {status.service}
                                </div>
                                <div style={{ 
                                  background: getSlotColor(status),
                                  color: 'white',
                                  padding: '0.125rem 0.25rem',
                                  borderRadius: '3px',
                                  fontSize: '0.6rem',
                                  display: 'inline-block'
                                }}>
                                  {status.status === 'confirmed' ? 'Booked' : status.status}
                                </div>
                              </div>
                            ) : status.type === 'blocked' ? (
                              <div style={{
                                background: getSlotBackground(status),
                                border: `2px solid ${getSlotColor(status)}`,
                                borderRadius: '6px',
                                padding: '0.5rem',
                                fontSize: '0.75rem',
                                height: '100%',
                                minHeight: '60px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: timeSlot.id !== '12:00' ? 'pointer' : 'default'
                              }}
                              onClick={() => timeSlot.id !== '12:00' && toggleSlotBlock(date, timeSlot)}
                            >
                              <div style={{ color: getSlotColor(status), fontWeight: 'bold' }}>
                                🚫 Blocked
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                                {status.reason}
                              </div>
                              {(
                                <button 
                                  onClick={() => toggleSlotBlock(date, timeSlot)}
                                  style={{
                                    background: '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    padding: '0.125rem 0.25rem',
                                    fontSize: '0.6rem',
                                    marginTop: '0.25rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Unblock
                                </button>
                              )}
                            </div>
                            ) : (
                              <div style={{
                                border: '2px dashed #4caf50',
                                borderRadius: '6px',
                                padding: '0.5rem',
                                fontSize: '0.75rem',
                                height: '100%',
                                minHeight: '60px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => toggleSlotBlock(date, timeSlot)}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#f1f8e9';
                                e.target.style.borderStyle = 'solid';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                                e.target.style.borderStyle = 'dashed';
                              }}
                            >
                              <div style={{ color: '#4caf50', fontSize: '1rem' }}>+</div>
                              <div style={{ fontSize: '0.7rem', color: '#666' }}>Available</div>
                              <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem' }}>
                                <button 
                                  onClick={() => handleBookAppointment(date, timeSlot)}
                                  style={{
                                    background: '#2196f3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    padding: '0.125rem 0.25rem',
                                    fontSize: '0.6rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Book
                                </button>
                                <button 
                                  onClick={() => toggleSlotBlock(date, timeSlot)}
                                  style={{
                                    background: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    padding: '0.125rem 0.25rem',
                                    fontSize: '0.6rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Block
                                </button>
                              </div>
                            </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
                </div>
              ) : (
                // Mobile Calendar Layout
                <div style={{ padding: windowWidth < 480 ? '0.75rem' : '1rem' }}>
                  {weekDates.map((date, dayIndex) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isToday = date.toDateString() === new Date().toDateString();
                    
                    return (
                      <div
                        key={dayIndex}
                        style={{
                          marginBottom: '1.5rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          background: isWeekend ? '#f9f9f9' : 'white'
                        }}
                      >
                        {/* Mobile Day Header */}
                        <div style={{
                          background: isToday ? '#e8f5e8' : '#f5f5f5',
                          padding: windowWidth < 480 ? '0.75rem' : '1rem',
                          borderBottom: '1px solid #e5e7eb',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: windowWidth < 480 ? '0.9rem' : '1rem',
                            fontWeight: 'bold',
                            color: isToday ? '#2e7d32' : '#333'
                          }}>
                            {date.toLocaleDateString('en-US', { weekday: 'long' })}
                          </div>
                          <div style={{
                            fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem',
                            fontWeight: 'bold',
                            color: isToday ? '#2e7d32' : '#333',
                            marginTop: '0.25rem'
                          }}>
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                          {isWeekend && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#999',
                              marginTop: '0.25rem'
                            }}>
                              Closed
                            </div>
                          )}
                        </div>

                        {/* Mobile Time Slots */}
                        {!isWeekend && (
                          <div style={{ padding: windowWidth < 480 ? '0.5rem' : '0.75rem' }}>
                            {timeSlots.map((timeSlot) => {
                              const status = getSlotStatus(date, timeSlot);
                              
                              return (
                                <div
                                  key={timeSlot.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: windowWidth < 480 ? '0.5rem' : '0.75rem',
                                    marginBottom: '0.5rem',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    background: getSlotBackground(status) || 'white'
                                  }}
                                >
                                  {/* Time */}
                                  <div style={{
                                    minWidth: windowWidth < 480 ? '70px' : '80px',
                                    fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#666'
                                  }}>
                                    {timeSlot.time}
                                  </div>

                                  {/* Status Content */}
                                  <div style={{ flex: 1, marginLeft: '1rem' }}>
                                    {status.type === 'booked' ? (
                                      <div>
                                        <div style={{
                                          fontWeight: 'bold',
                                          color: getSlotColor(status),
                                          fontSize: windowWidth < 480 ? '0.85rem' : '0.9rem',
                                          marginBottom: '0.25rem'
                                        }}>
                                          {status.customerName}
                                        </div>
                                        <div style={{
                                          color: '#666',
                                          fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                                          marginBottom: '0.25rem'
                                        }}>
                                          {status.service}
                                        </div>
                                        <span style={{
                                          background: getSlotColor(status),
                                          color: 'white',
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '12px',
                                          fontSize: windowWidth < 480 ? '0.7rem' : '0.75rem',
                                          fontWeight: '600'
                                        }}>
                                          {status.status === 'confirmed' ? 'Booked' : status.status}
                                        </span>
                                      </div>
                                    ) : status.type === 'blocked' ? (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                          <div style={{
                                            color: getSlotColor(status),
                                            fontWeight: 'bold',
                                            fontSize: windowWidth < 480 ? '0.85rem' : '0.9rem'
                                          }}>
                                            🚫 Blocked
                                          </div>
                                          <div style={{
                                            fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                                            color: '#666'
                                          }}>
                                            {status.reason}
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => toggleSlotBlock(date, timeSlot)}
                                          style={{
                                            background: '#4caf50',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: windowWidth < 480 ? '0.25rem 0.5rem' : '0.375rem 0.75rem',
                                            fontSize: windowWidth < 480 ? '0.7rem' : '0.75rem',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Unblock
                                        </button>
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{
                                          color: '#4caf50',
                                          fontSize: windowWidth < 480 ? '0.85rem' : '0.9rem'
                                        }}>
                                          ✅ Available
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                          <button
                                            onClick={() => handleBookAppointment(date, timeSlot)}
                                            style={{
                                              background: '#2196f3',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: windowWidth < 480 ? '0.25rem 0.5rem' : '0.375rem 0.75rem',
                                              fontSize: windowWidth < 480 ? '0.7rem' : '0.75rem',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Book
                                          </button>
                                          <button
                                            onClick={() => toggleSlotBlock(date, timeSlot)}
                                            style={{
                                              background: '#ff9800',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: windowWidth < 480 ? '0.25rem 0.5rem' : '0.375rem 0.75rem',
                                              fontSize: windowWidth < 480 ? '0.7rem' : '0.75rem',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Block
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Bulk Actions */}
        <div className="card" style={{ 
          marginTop: windowWidth < 768 ? '1rem' : '2rem',
          padding: windowWidth < 480 ? '1rem' : '1.5rem'
        }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            fontSize: windowWidth < 480 ? '1.1rem' : '1.25rem' 
          }}>
            Bulk Actions
          </h3>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 480 ? 'column' : 'row',
            gap: windowWidth < 480 ? '0.75rem' : '1rem', 
            flexWrap: 'wrap' 
          }}>
            <button 
              className="btn btn-secondary"
              style={{
                fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                padding: windowWidth < 480 ? '0.75rem' : '0.75rem 1rem',
                width: windowWidth < 480 ? '100%' : 'auto'
              }}
            >
              Block All Available
            </button>
            <button 
              className="btn btn-primary"
              style={{
                fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                padding: windowWidth < 480 ? '0.75rem' : '0.75rem 1rem',
                width: windowWidth < 480 ? '100%' : 'auto'
              }}
            >
              Unblock All Blocked
            </button>
            <button 
              className="btn" 
              style={{ 
                background: '#9c27b0', 
                color: 'white',
                fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                padding: windowWidth < 480 ? '0.75rem' : '0.75rem 1rem',
                width: windowWidth < 480 ? '100%' : 'auto'
              }}
            >
              Copy from Another Date
            </button>
          </div>
        </div>

        {/* Booking Modal */}
        {showBookingModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: windowWidth < 768 ? '1rem 0.5rem' : '2rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: windowWidth < 768 ? '8px' : '12px',
              padding: windowWidth < 480 ? '1.5rem' : '2rem',
              maxWidth: windowWidth < 768 ? '100%' : '500px',
              width: windowWidth < 768 ? '100%' : '90%',
              maxHeight: windowWidth < 768 ? 'calc(100vh - 2rem)' : '80vh',
              overflow: 'auto',
              margin: windowWidth < 768 ? '0' : 'auto'
            }}>
              <h3 style={{ 
                marginBottom: '1.5rem', 
                fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem', 
                color: '#333',
                wordBreak: 'break-word'
              }}>
                {bookingSuccess ? '✅ Appointment Booked!' : 'Book Appointment'}
              </h3>
              
              {selectedSlotForBooking && (
                <div style={{ 
                  background: bookingSuccess ? '#e8f5e8' : '#f5f5f5', 
                  padding: '1rem', 
                  borderRadius: '6px', 
                  marginBottom: '1.5rem',
                  border: bookingSuccess ? '1px solid #4caf50' : 'none'
                }}>
                  <strong>Selected Slot:</strong> {selectedSlotForBooking.timeSlot.time} on {new Date(selectedSlotForBooking.date).toLocaleDateString()}
                  {bookingSuccess && (
                    <div style={{ color: '#4caf50', marginTop: '0.5rem', fontWeight: '600' }}>
                      Appointment successfully created!
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {bookingError && (
                <div style={{
                  background: '#ffebee',
                  border: '1px solid #f44336',
                  color: '#d32f2f',
                  padding: '1rem',
                  borderRadius: '6px',
                  marginBottom: '1.5rem',
                  fontSize: '0.875rem'
                }}>
                  {bookingError}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={bookingForm.customerName}
                  onChange={(e) => setBookingForm({...bookingForm, customerName: e.target.value})}
                  disabled={bookingLoading || bookingSuccess}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: (bookingLoading || bookingSuccess) ? '#f5f5f5' : 'white',
                    color: (bookingLoading || bookingSuccess) ? '#999' : '#333'
                  }}
                  placeholder="Enter customer name"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Customer Email
                </label>
                <input
                  type="email"
                  value={bookingForm.customerEmail}
                  onChange={(e) => setBookingForm({...bookingForm, customerEmail: e.target.value})}
                  disabled={bookingLoading || bookingSuccess}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: (bookingLoading || bookingSuccess) ? '#f5f5f5' : 'white',
                    color: (bookingLoading || bookingSuccess) ? '#999' : '#333'
                  }}
                  placeholder="Enter customer email"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Service *
                </label>
                <select
                  value={bookingForm.service}
                  onChange={(e) => setBookingForm({...bookingForm, service: e.target.value})}
                  disabled={bookingLoading || bookingSuccess}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: (bookingLoading || bookingSuccess) ? '#f5f5f5' : 'white',
                    color: (bookingLoading || bookingSuccess) ? '#999' : '#333'
                  }}
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Notes
                </label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                  disabled={bookingLoading || bookingSuccess}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    minHeight: '80px',
                    resize: 'vertical',
                    background: (bookingLoading || bookingSuccess) ? '#f5f5f5' : 'white',
                    color: (bookingLoading || bookingSuccess) ? '#999' : '#333'
                  }}
                  placeholder="Additional notes (optional)"
                />
              </div>

              <div style={{ 
                display: 'flex', 
                flexDirection: windowWidth < 480 ? 'column' : 'row',
                gap: windowWidth < 480 ? '0.75rem' : '1rem', 
                justifyContent: 'flex-end' 
              }}>
                <button
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedSlotForBooking(null);
                    setBookingForm({ customerName: '', customerEmail: '', service: '', notes: '' });
                    setBookingError('');
                    setBookingSuccess(false);
                  }}
                  disabled={bookingLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: bookingLoading ? '#f5f5f5' : 'white',
                    color: bookingLoading ? '#999' : '#666',
                    cursor: bookingLoading ? 'not-allowed' : 'pointer',
                    fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                    width: windowWidth < 480 ? '100%' : 'auto'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitBooking}
                  disabled={bookingLoading || bookingSuccess}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: bookingSuccess ? '#4caf50' : (bookingLoading ? '#90caf9' : '#2196f3'),
                    color: 'white',
                    cursor: (bookingLoading || bookingSuccess) ? 'not-allowed' : 'pointer',
                    fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    width: windowWidth < 480 ? '100%' : 'auto'
                  }}
                >
                  {bookingLoading && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {bookingSuccess ? 'Booked!' : (bookingLoading ? 'Booking...' : 'Book Appointment')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SlotManagement;
